/**
 * L5a — displayed at the very top of AppLayout whenever the current
 * session was minted by the superadmin console for an impersonation
 * handoff. It reads the marker written by main.tsx into sessionStorage,
 * decodes the JWT for the expiry, runs a live countdown, and calls
 * /v1/my/impersonation/end to close the session cleanly. Expiry also
 * clears the local state and reloads to the login page.
 *
 * The banner never renders during a normal tenant login (no marker in
 * sessionStorage). Purple + non-dismissible so the impersonating
 * operator can't miss it.
 */

import { apiFetch } from '@/api/core'
import { useAuthStore } from '@/stores/auth.store'
import { ShieldAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'impersonation_token'

interface ImpersonationClaims {
  exp?: number
  imp_by?: string
  imp_jti?: string
  sub?: string
}

function decodeJwt(token: string): ImpersonationClaims | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as ImpersonationClaims
  } catch {
    return null
  }
}

function fmt(ms: number): string {
  if (ms <= 0) return '00:00'
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

export function ImpersonationBanner() {
  const [now, setNow] = useState<number>(() => Date.now())
  const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null

  const claims = useMemo(() => (token ? decodeJwt(token) : null), [token])
  const expiresAt = claims?.exp ? claims.exp * 1000 : null

  useEffect(() => {
    if (!expiresAt) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [expiresAt])

  useEffect(() => {
    if (!expiresAt) return
    if (now < expiresAt) return
    // Expired — clear and force a login.
    sessionStorage.removeItem(STORAGE_KEY)
    useAuthStore.getState().clearAuth()
    window.location.href = '/auth/login'
  }, [now, expiresAt])

  if (!token || !claims) return null

  const remaining = expiresAt ? expiresAt - now : 0

  async function end() {
    try {
      await apiFetch('/v1/my/impersonation/end', { method: 'POST' })
    } catch {
      // even if the server call fails, we clear locally
    }
    sessionStorage.removeItem(STORAGE_KEY)
    useAuthStore.getState().clearAuth()
    window.location.href = '/auth/login'
  }

  return (
    <div className="flex items-center gap-3 border-b border-purple-800 bg-purple-700 px-4 py-2 text-sm text-white">
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
      <span className="font-semibold">Session IMPACT en cours</span>
      <span className="opacity-90 hidden sm:inline">
        Un opérateur IMPACT est connecté sur votre compte à des fins de support.
      </span>
      <span className="ml-auto font-mono tabular-nums" title="Temps restant">
        {fmt(remaining)}
      </span>
      <button
        type="button"
        onClick={end}
        className="min-h-[36px] rounded-md bg-purple-900 px-3 py-1 text-xs font-medium hover:bg-purple-950"
      >
        Terminer la session
      </button>
    </div>
  )
}
